import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import { P4 } from '@repo/ui-kit/components/core/Typography';

import PauseButtonIcon from 'components/utilities/icons/pauseButton';
import PlayButtonIcon from 'components/utilities/icons/playButton';

import {
  PlayButton,
  ProgressBar,
  Video,
  VideoControls,
  VideoWrapper,
} from './styles';
import { ProgressState, VideoPlayerProps } from './types';

const padTo2Digits = (num: number): string => num.toString().padStart(2, '0');

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, controls }) => {
  const [progress, setProgress] = useState<ProgressState>({
    percent: 0,
    milliseconds: 0,
  });
  const [isPlaying, setPlaying] = useState<boolean>(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideo = () => {
    if (!videoRef.current) {
      return;
    }

    setPlaying((prevState) => !prevState);
  };

  useEffect(() => {
    if (isPlaying) {
      videoRef.current?.play();
    } else {
      videoRef.current?.pause();
    }
  }, [isPlaying]);

  const handleVideoProgress = (event: ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) {
      return;
    }

    const manualChange = Number(event.target.value);
    videoRef.current.currentTime =
      (videoRef.current.duration / 100) * manualChange;
    setPlaying(!!manualChange);
  };

  const handleOnTimeUpdate = () => {
    if (!videoRef.current) {
      return;
    }

    const percent =
      (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setProgress({
      percent: percent,
      milliseconds: videoRef.current.currentTime * 1000,
    });
  };

  const time = {
    seconds: padTo2Digits(Math.floor(progress.milliseconds / 1000) % 60),
    minutes: padTo2Digits(Math.floor(progress.milliseconds / 1000 / 60)),
  };

  return (
    <VideoWrapper>
      <Video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        onTimeUpdate={handleOnTimeUpdate}
      >
        <source src={src} type="video/mp4" />
      </Video>
      {controls && (
        <VideoControls>
          <PlayButton onClick={handleVideo}>
            {isPlaying ? <PauseButtonIcon /> : <PlayButtonIcon />}
          </PlayButton>
          <P4>
            {time.minutes}:{time.seconds}
          </P4>
          <ProgressBar
            max="100"
            min="0"
            type="range"
            value={progress.percent}
            onChange={handleVideoProgress}
          />
        </VideoControls>
      )}
    </VideoWrapper>
  );
};
